import type { ZodType } from 'zod'
import {
  apiErrorBodySchema,
  apiErrorEnvelopeSchema,
  type ApiErrorBody,
} from '@lib/dto/api-error.dto'
import type { LoanIssueDto } from '@lib/dto/loan-issue.dto'
import type { UnderwritingFindingDto } from '@lib/dto/underwriting-rule.dto'
import {
  API_ERROR_CODE,
  type ApiErrorCode,
} from '@lib/enums/api-error-code.enum'
import type { Thrown } from '@lib/helpers/thrown'
import { tokenStorage } from './tokenStorage'
import logger, { toErrorMessage } from './logger'

/**
 * The fetch-backed HTTP client for the web app. Every response is validated
 * with a Zod schema at the network boundary — the client never launders a raw
 * `.json()` payload into a concrete type (`app/no-untyped-fetch-json`). The
 * schema is both the validator and the source of the returned type.
 *
 * Usage:
 *   const loan = await api.get('/loans/1', loanDtoSchema) // LoanDto
 *   const maybe = await api.getNullable('/loans/1', loanDtoSchema) // LoanDto | null
 *
 * `get<T>` asserts the endpoint always returns a `T`. For endpoints that can
 * legitimately return no content (empty body / 204 / a bare `null`), use
 * `getNullable<T>`, which resolves an absent body to `null` and types the
 * result `T | null` (`app/prefer-get-nullable`).
 */

/**
 * The base URL of the API. Overridable per environment via `VITE_API_URL`.
 * In Vite dev, unset `VITE_API_URL` goes through `/__api` so the proxy can
 * follow whatever port the local API actually bound.
 */
const API_BASE_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? '/__api' : 'http://localhost:8000')

export type { ApiErrorBody }

/**
 * One row from a status-block error. Findings and issues are different DTOs;
 * the discriminant keeps them from collapsing to `{ message: string }`.
 */
export type StatusBlockItem =
  | { source: 'finding'; item: UnderwritingFindingDto }
  | { source: 'issue'; item: LoanIssueDto }

/** Validated block rows on an error body, findings first. */
export function statusBlockItems(body: ApiErrorBody): StatusBlockItem[] {
  const items: StatusBlockItem[] = []
  for (const item of body.findings ?? []) {
    items.push({ source: 'finding', item })
  }
  for (const item of body.issues ?? []) {
    items.push({ source: 'issue', item })
  }
  return items
}

/** Stable list key for a {@link StatusBlockItem}. */
export function statusBlockItemKey(
  item: StatusBlockItem,
  index: number,
): string {
  if (item.source === 'finding') return `finding:${item.item.ruleInstanceId}`
  return `issue:${item.item.section}:${item.item.code}:${index}`
}

export function isUnderwritingBlockedError(
  error: Error | ApiError | null | undefined,
): error is ApiError & {
  body: ApiErrorBody & {
    code: ApiErrorCode
  }
} {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    (error.body.code === API_ERROR_CODE.UNDERWRITING_BLOCKED ||
      error.body.code === API_ERROR_CODE.LOAN_ISSUE_BLOCKED)
  )
}

/**
 * Thrown for any non-2xx response (and for network failures, with `status: 0`).
 * Carries the HTTP status and the parsed error body so callers and React Query
 * error states can branch on `status` or surface `body.message`.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** Type guard for {@link ApiError}, for anything thrown or a query's error. */
export function isApiError(error: Thrown): error is ApiError {
  return error instanceof ApiError
}

/** Options accepted by every request method. */
export interface RequestOptions {
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>
  /** AbortController signal for cancellation. */
  signal?: AbortSignal
}

/** A JSON-serializable request body — the call site supplies its concrete shape. */
export type RequestBody = object

/** Body types accepted by {@link send}. FormData is sent multipart (no JSON Content-Type). */
type SendBody = RequestBody | FormData

/**
 * Parse a text body as JSON, treating an empty body as `null`. Returns `any`
 * (JSON.parse) by design: the value is immediately handed to `schema.parse`,
 * which validates it and produces the typed result.
 */
function parseJson(text: string) {
  return text.trim() === '' ? null : JSON.parse(text)
}

/** Extract a typed error body from a raw response text (never throws). */
function readErrorBody(text: string): ApiErrorBody {
  if (text.trim() === '') return {}
  try {
    const json = JSON.parse(text)
    const full = apiErrorBodySchema.safeParse(json)
    if (full.success) return full.data
    const envelope = apiErrorEnvelopeSchema.safeParse(json)
    if (envelope.success) {
      logger.warn('API error block list failed schema validation', {
        paths: full.error.issues
          .map((issue) => issue.path.join('.'))
          .join(',')
          .slice(0, 200),
      })
      return envelope.data
    }
    return { message: text }
  } catch {
    return { message: text }
  }
}

/**
 * Perform the HTTP request, applying the base URL, auth header, and JSON body
 * encoding, and translating a failed response into a thrown {@link ApiError}.
 * Returns the raw response text so each method can parse it with its schema.
 */
async function send(
  method: string,
  path: string,
  body: SendBody | undefined,
  options: RequestOptions | undefined,
): Promise<string> {
  const headers: Record<string, string> = { ...options?.headers }
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  if (
    body !== undefined &&
    !isFormData &&
    headers['Content-Type'] === undefined
  ) {
    headers['Content-Type'] = 'application/json'
  }

  const token = tokenStorage.get()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined
        ? { body: isFormData ? body : JSON.stringify(body) }
        : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
    })
  } catch (error) {
    // Rethrow caller-initiated cancellation so callers can detect it.
    if (error instanceof Error && error.name === 'AbortError') throw error
    const message = toErrorMessage(error)
    logger.error('Network request failed', { path, error: message })
    throw new ApiError(0, { message })
  }

  const text = await response.text()

  if (!response.ok) {
    // A 401 means the stored token is no longer valid — drop it so the app
    // falls back to the signed-out state on the next auth check.
    if (response.status === 401) tokenStorage.clear()
    const errorBody = readErrorBody(text)
    const context = {
      path,
      status: response.status,
      message: errorBody.message ?? errorBody.error ?? null,
    }
    // An expired session is routine (sign in again), and any other 4xx is the
    // API answering as designed (a blocked status change, a validation message).
    // Only server errors are errors to report.
    if (response.status === 401) logger.info('API request unauthorized', context)
    else if (response.status < 500) logger.info('API request refused', context)
    else logger.error('API request failed', context)
    throw new ApiError(response.status, errorBody)
  }

  return text
}

/** Binary download with auth (for loan document files). */
async function sendBlob(
  method: string,
  path: string,
  options?: RequestOptions,
): Promise<Blob> {
  const headers: Record<string, string> = { ...options?.headers }
  const token = tokenStorage.get()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      ...(options?.signal ? { signal: options.signal } : {}),
    })
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error
    throw new ApiError(0, { message: toErrorMessage(error) })
  }

  if (!response.ok) {
    if (response.status === 401) tokenStorage.clear()
    const text = await response.text()
    throw new ApiError(response.status, readErrorBody(text))
  }
  return response.blob()
}

export const api = {
  /** GET a resource that is always present; validates the body with `schema`. */
  async get<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('GET', path, undefined, options)
    return schema.parse(parseJson(text))
  },

  /**
   * GET a resource that may be absent. An empty body / 204 / bare `null`
   * resolves to `null`; otherwise the body is validated with `schema`.
   */
  async getNullable<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T | null> {
    const text = await send('GET', path, undefined, options)
    const parsed = parseJson(text)
    if (parsed === null) return null
    return schema.parse(parsed)
  },

  /** POST a JSON body (or FormData) and validate the response with `schema`. */
  async post<T>(
    path: string,
    body: RequestBody | FormData,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('POST', path, body, options)
    return schema.parse(parseJson(text))
  },

  /** GET a binary response (authenticated download). */
  async getBlob(path: string, options?: RequestOptions): Promise<Blob> {
    return sendBlob('GET', path, options)
  },

  /** PUT a JSON body and validate the response with `schema`. */
  async put<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('PUT', path, body, options)
    return schema.parse(parseJson(text))
  },

  /** PATCH a JSON body and validate the response with `schema`. */
  async patch<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('PATCH', path, body, options)
    return schema.parse(parseJson(text))
  },

  /** DELETE a resource. Resolves once the server confirms; no body is returned. */
  async del(path: string, options?: RequestOptions): Promise<void> {
    await send('DELETE', path, undefined, options)
  },

  /** DELETE and validate a JSON response body with `schema`. */
  async delJson<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('DELETE', path, undefined, options)
    return schema.parse(parseJson(text))
  },
}
